import React, { Component } from "react";
import {
    Box,
    Paper,
    Typography,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Link as MuiLink,
    Divider,
    Chip,
    Stack,
    Alert,
    AlertTitle,
    CircularProgress,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    IconButton
} from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import EditIcon from '@mui/icons-material/Edit';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import rrulePlugin from '@fullcalendar/rrule';
import mockEvents from './mockEvents.json';
import { isAuthenticated } from "../auth";
import { groupDetails } from "../group/apigroup";
import { getCalendarEvents, updateCalendarEvent } from "./apiEvents";

// Committee colors mapping
const committeeColors = {
    'general': '#4caf50',
    'physical': '#993399',
    'electrical': '#f44336',
    'civil': '#795548',
    'technology': '#339966',
    'business': '#000099',
    'bizstrategy': '#000099', // Business Strategy
    'innovation': '#FF6600',
    'innovative': '#FF6600' // Innovation
};

// Map committeeType to groupId
const committeeToGroupId = {
    'physical': '5e2a5e764175b97d84ec6cae',
    'electrical': '5e44050f4e6ed37ab467d0f2',
    'technology': '5e44052e4e6ed37ab467d0f3',
    'civil': '5e4405864e6ed37ab467d0f4',
    'business': '5e4406454e6ed37ab467d0f5',
    'bizstrategy': '5e4406454e6ed37ab467d0f5', // Business Strategy
    'leadership': '5e5591a97ff73f6d34835a05',
    'innovation': '639242d4971a30726884fd85',
    'innovative': '639242d4971a30726884fd85', // Innovation
    'general': '5e5591a97ff73f6d34835a05' // Same as leadership for backwards compatibility
};

class EventCalendar extends Component {
    constructor(props) {
        super(props)
        this.state = {
            isAdmin: this.props.isAdmin,
            data: mockEvents,
            mainResourceName: '',
            resources: this.props.resourceList,
            selectedEvent: null,
            selectedEventId: null,
            dialogOpen: false,
            userId: isAuthenticated() && isAuthenticated().userId,
            userEmail: isAuthenticated() && isAuthenticated().email,
            isMember: false,
            isChairOrCoChair: false,
            checkingMembership: true,
            currentGroupId: null,
            editing: false,
            editFields: {},
            editSaving: false,
            editError: ''
        }
        this.changeMainResource = this.changeMainResource.bind(this);
        this.handleEventClick = this.handleEventClick.bind(this);
        this.handleCloseDialog = this.handleCloseDialog.bind(this);
        this.checkMembership = this.checkMembership.bind(this);
    }

    componentDidMount() {
        // Load events from backend API, fall back to mock data
        getCalendarEvents().then(data => {
            if (data && !data.error && Array.isArray(data)) {
                this.setState({ data });
            }
        }).catch(() => {});
    }

    changeMainResource(mainResourceName) {
        this.setState({ mainResourceName });
    }

    handleEventClick(info) {
        const committeeType = info.event.extendedProps.committeeType;
        // Prefer the committee the event itself names - it is the same record the
        // server authorises against, so the Edit button cannot promise access the
        // save will refuse. The lookup below only covers events predating that field.
        const groupId = info.event.extendedProps.groupId || committeeToGroupId[committeeType];

        this.setState({
            selectedEvent: info.event.extendedProps,
            selectedEventId: info.event.id,
            dialogOpen: true,
            checkingMembership: true,
            currentGroupId: groupId,
            editing: false,
            editFields: {},
            editError: ''
        }, () => {
            this.checkMembership(groupId);
        });
    }

    checkMembership(groupId) {
        if (!groupId || !this.state.userId) {
            this.setState({
                isMember: false,
                isChairOrCoChair: false,
                checkingMembership: false
            });
            return;
        }

        groupDetails(groupId).then(data => {
            if (data.error) {
                this.setState({
                    isMember: false,
                    isChairOrCoChair: false,
                    checkingMembership: false
                });
            } else {
                const members = data.members || [];
                const isMember = members.some(member => member._id === this.state.userId);
                const isChair = data.chair && data.chair._id === this.state.userId;
                const isCoChair = data.cochair && data.cochair._id === this.state.userId;
                const isAdmin = isAuthenticated() && isAuthenticated().role === 'admin';
                this.setState({
                    isMember: isMember || isChair || isCoChair || isAdmin,
                    isChairOrCoChair: isChair || isCoChair || isAdmin,
                    checkingMembership: false
                });
            }
        }).catch(err => {
            this.setState({
                isMember: false,
                isChairOrCoChair: false,
                checkingMembership: false
            });
        });
    }

    handleEditToggle = () => {
        const { selectedEvent } = this.state;
        const isRecurring = !!selectedEvent.rrule;
        // datetime-local wants "YYYY-MM-DDTHH:MM"; stored values carry seconds.
        const toLocalInput = (iso) => (iso || '').slice(0, 16);
        this.setState({
            editing: true,
            editFields: {
                notes: selectedEvent.notes || '',
                meetingType: selectedEvent.meetingType || (selectedEvent.teamsLink ? 'teams' : 'zoom'),
                zoomId: selectedEvent.zoomId || '',
                zoomPass: selectedEvent.zoompass || '',
                zoomLink: selectedEvent.zoomLink || '',
                zoomRoom: selectedEvent.zoomRoom || '',
                teamsId: selectedEvent.teamsId || '',
                teamsPasscode: selectedEvent.teamsPasscode || '',
                teamsLink: selectedEvent.teamsLink || '',
                // Schedule (Eastern wall-clock; backend treats these as America/New_York).
                isRecurring,
                startDateTime: isRecurring ? toLocalInput(selectedEvent.rrule.dtstart) : '',
                duration: selectedEvent.duration || '01:00',
                startDate: !isRecurring ? toLocalInput(selectedEvent.startDate) : '',
                endDate: !isRecurring ? toLocalInput(selectedEvent.endDate) : ''
            }
        });
    };

    // Human-readable summary of a fixed recurrence pattern (shown read-only).
    recurrenceSummary = (r) => {
        if (!r) return '';
        const ORD = { '1': '1st', '2': '2nd', '3': '3rd', '4': '4th', '-1': 'last' };
        const DAY = { mo: 'Monday', tu: 'Tuesday', we: 'Wednesday', th: 'Thursday', fr: 'Friday', sa: 'Saturday', su: 'Sunday' };
        const freq = String(r.freq || '').toLowerCase();
        if (r.interval && r.interval > 1) return `every ${r.interval} ${freq.replace(/ly$/, '')}s`;
        if (Array.isArray(r.byweekday) && r.byweekday.length) {
            const day = DAY[String(r.byweekday[0]).toLowerCase()] || r.byweekday[0];
            const pos = r.bysetpos != null && ORD[String(r.bysetpos)] ? `${ORD[String(r.bysetpos)]} ` : '';
            return `${pos}${day}, ${freq}`;
        }
        return freq;
    };

    handleEditFieldChange = (field) => (e) => {
        this.setState({
            editFields: { ...this.state.editFields, [field]: e.target.value }
        });
    };

    handleEditSave = () => {
        const { selectedEventId, editFields, selectedEvent } = this.state;
        this.setState({ editSaving: true, editError: '' });

        // Meeting-detail fields always sent; schedule reassembled from the form.
        const updates = {
            notes: editFields.notes,
            meetingType: editFields.meetingType,
            zoomId: editFields.zoomId,
            zoomPass: editFields.zoomPass,
            zoomLink: editFields.zoomLink,
            zoomRoom: editFields.zoomRoom,
            teamsId: editFields.teamsId,
            teamsPasscode: editFields.teamsPasscode,
            teamsLink: editFields.teamsLink
        };
        // datetime-local omits seconds; restore them for the backend.
        const withSeconds = (v) => (v && v.length === 16 ? `${v}:00` : v);
        if (editFields.isRecurring) {
            if (editFields.startDateTime) {
                updates.rrule = { ...selectedEvent.rrule, dtstart: withSeconds(editFields.startDateTime) };
            }
            updates.duration = editFields.duration;
        } else {
            if (editFields.startDate) updates.startDate = withSeconds(editFields.startDate);
            if (editFields.endDate) updates.endDate = withSeconds(editFields.endDate);
        }

        updateCalendarEvent(selectedEventId, updates).then(data => {
            if (data && data.error) {
                this.setState({ editError: data.error, editSaving: false });
            } else {
                // Refresh events from backend
                getCalendarEvents().then(events => {
                    if (events && !events.error && Array.isArray(events)) {
                        this.setState({ data: events });
                    }
                });
                this.handleCloseDialog();
            }
        }).catch(() => {
            this.setState({ editError: 'Failed to save changes', editSaving: false });
        });
    };

    handleCloseDialog() {
        this.setState({
            selectedEvent: null,
            dialogOpen: false,
            isMember: false,
            checkingMembership: true,
            currentGroupId: null
        });
    }

    // Transform data for FullCalendar format
    getEvents() {
        const { data, mainResourceName } = this.state;

        if (!data || !Array.isArray(data)) {
            return [];
        }

        // Filter and transform events
        const events = data
            .filter(event => {
                if (!mainResourceName) return true;
                // Handle filter aliases
                if (mainResourceName === 'business' && event.committeeType === 'bizstrategy') return true;
                if (mainResourceName === 'innovation' && event.committeeType === 'innovative') return true;
                return event.committeeType === mainResourceName;
            })
            .map(event => {

                const baseEvent = {
                    id: event.id,
                    title: event.title,
                    backgroundColor: committeeColors[event.committeeType] || '#4caf50',
                    borderColor: committeeColors[event.committeeType] || '#4caf50',
                    extendedProps: {
                        notes: event.notes,
                        meetingType: event.meetingType || (event.teamsLink ? 'teams' : 'zoom'),
                        zoomId: event.zoomId,
                        zoompass: event.zoomPass || event.zoompass,
                        zoomLink: event.zoomLink,
                        zoomRoom: event.zoomRoom,
                        teamsLink: event.teamsLink,
                        teamsId: event.teamsId,
                        teamsPasscode: event.teamsPasscode,
                        committeeType: event.committeeType,
                        groupId: event.groupId,
                        icsDownloadUrl: event.icsDownloadUrl,
                        // The edit dialog reads the schedule from extendedProps.
                        // FullCalendar's own rrule/duration/start/end are set at the
                        // top level below and are not visible to it, so the raw
                        // values have to be carried through here as well.
                        rrule: event.rrule,
                        duration: event.duration,
                        startDate: event.startDate,
                        endDate: event.endDate
                    }
                };

                // Check if this is a recurring event
                if (event.rrule) {
                    return {
                        ...baseEvent,
                        rrule: event.rrule,
                        duration: event.duration || '01:00'
                    };
                } else {
                    // Regular one-time event
                    return {
                        ...baseEvent,
                        start: event.startDate,
                        end: event.endDate
                    };
                }
            });

        
        return events;
    }

    render() {
        const { mainResourceName, selectedEvent, dialogOpen } = this.state;

        return (
            <Box>
                {/* Committee Filter Chips */}
                <Box sx={{ mb: 3 }}>
                    <Typography variant="body1" sx={{ mb: 2, fontWeight: 600 }}>
                        Filter by Committee:
                    </Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ gap: 1 }}>
                        <Chip
                            label="All Committees"
                            onClick={() => this.changeMainResource('')}
                            color={mainResourceName === '' ? 'primary' : 'default'}
                            variant={mainResourceName === '' ? 'filled' : 'outlined'}
                            sx={{
                                fontWeight: mainResourceName === '' ? 600 : 400,
                                backgroundColor: mainResourceName === '' ? '#003E7E' : 'transparent',
                                color: mainResourceName === '' ? 'white' : 'inherit',
                                '&:hover': {
                                    backgroundColor: mainResourceName === '' ? '#FF6600' : 'rgba(0, 62, 126, 0.08)'
                                }
                            }}
                        />
                        <Chip
                            label="Physical"
                            onClick={() => this.changeMainResource('physical')}
                            sx={{
                                backgroundColor: mainResourceName === 'physical' ? '#993399' : 'transparent',
                                color: mainResourceName === 'physical' ? 'white' : '#993399',
                                borderColor: '#993399',
                                fontWeight: mainResourceName === 'physical' ? 600 : 400,
                                '&:hover': {
                                    backgroundColor: mainResourceName === 'physical' ? '#7a297a' : 'rgba(153, 51, 153, 0.08)'
                                }
                            }}
                            variant={mainResourceName === 'physical' ? 'filled' : 'outlined'}
                        />
                        <Chip
                            label="Electrical"
                            onClick={() => this.changeMainResource('electrical')}
                            sx={{
                                backgroundColor: mainResourceName === 'electrical' ? '#f44336' : 'transparent',
                                color: mainResourceName === 'electrical' ? 'white' : '#f44336',
                                borderColor: '#f44336',
                                fontWeight: mainResourceName === 'electrical' ? 600 : 400,
                                '&:hover': {
                                    backgroundColor: mainResourceName === 'electrical' ? '#d32f2f' : 'rgba(244, 67, 54, 0.08)'
                                }
                            }}
                            variant={mainResourceName === 'electrical' ? 'filled' : 'outlined'}
                        />
                        <Chip
                            label="Technology"
                            onClick={() => this.changeMainResource('technology')}
                            sx={{
                                backgroundColor: mainResourceName === 'technology' ? '#339966' : 'transparent',
                                color: mainResourceName === 'technology' ? 'white' : '#339966',
                                borderColor: '#339966',
                                fontWeight: mainResourceName === 'technology' ? 600 : 400,
                                '&:hover': {
                                    backgroundColor: mainResourceName === 'technology' ? '#2d8659' : 'rgba(51, 153, 102, 0.08)'
                                }
                            }}
                            variant={mainResourceName === 'technology' ? 'filled' : 'outlined'}
                        />
                        <Chip
                            label="Civil"
                            onClick={() => this.changeMainResource('civil')}
                            sx={{
                                backgroundColor: mainResourceName === 'civil' ? '#795548' : 'transparent',
                                color: mainResourceName === 'civil' ? 'white' : '#795548',
                                borderColor: '#795548',
                                fontWeight: mainResourceName === 'civil' ? 600 : 400,
                                '&:hover': {
                                    backgroundColor: mainResourceName === 'civil' ? '#6d4c41' : 'rgba(121, 85, 72, 0.08)'
                                }
                            }}
                            variant={mainResourceName === 'civil' ? 'filled' : 'outlined'}
                        />
                        <Chip
                            label="Business Strategy"
                            onClick={() => this.changeMainResource('business')}
                            sx={{
                                backgroundColor: mainResourceName === 'business' ? '#000099' : 'transparent',
                                color: mainResourceName === 'business' ? 'white' : '#000099',
                                borderColor: '#000099',
                                fontWeight: mainResourceName === 'business' ? 600 : 400,
                                '&:hover': {
                                    backgroundColor: mainResourceName === 'business' ? '#000080' : 'rgba(0, 0, 153, 0.08)'
                                }
                            }}
                            variant={mainResourceName === 'business' ? 'filled' : 'outlined'}
                        />
                        <Chip
                            label="General"
                            onClick={() => this.changeMainResource('general')}
                            sx={{
                                backgroundColor: mainResourceName === 'general' ? '#4caf50' : 'transparent',
                                color: mainResourceName === 'general' ? 'white' : '#4caf50',
                                borderColor: '#4caf50',
                                fontWeight: mainResourceName === 'general' ? 600 : 400,
                                '&:hover': {
                                    backgroundColor: mainResourceName === 'general' ? '#43a047' : 'rgba(76, 175, 80, 0.08)'
                                }
                            }}
                            variant={mainResourceName === 'general' ? 'filled' : 'outlined'}
                        />
                        <Chip
                            label="Innovation"
                            onClick={() => this.changeMainResource('innovation')}
                            sx={{
                                backgroundColor: mainResourceName === 'innovation' ? '#FF6600' : 'transparent',
                                color: mainResourceName === 'innovation' ? 'white' : '#FF6600',
                                borderColor: '#FF6600',
                                fontWeight: mainResourceName === 'innovation' ? 600 : 400,
                                '&:hover': {
                                    backgroundColor: mainResourceName === 'innovation' ? '#e55a00' : 'rgba(255, 102, 0, 0.08)'
                                }
                            }}
                            variant={mainResourceName === 'innovation' ? 'filled' : 'outlined'}
                        />
                    </Stack>
                </Box>

                {/* Calendar */}
                <Paper
                    elevation={2}
                    sx={{
                        p: 3,
                        borderRadius: 2,
                        '& .fc': {
                            fontFamily: 'inherit'
                        },
                        '& .fc-toolbar-title': {
                            fontSize: '1.5rem',
                            fontWeight: 600,
                            color: '#003E7E'
                        },
                        '& .fc-button': {
                            backgroundColor: '#003E7E',
                            borderColor: '#003E7E',
                            textTransform: 'none',
                            '&:hover': {
                                backgroundColor: '#FF6600',
                                borderColor: '#FF6600'
                            },
                            '&:focus': {
                                boxShadow: 'none'
                            }
                        },
                        '& .fc-button-active': {
                            backgroundColor: '#FF6600 !important',
                            borderColor: '#FF6600 !important'
                        },
                        '& .fc-event': {
                            cursor: 'pointer',
                            '&:hover': {
                                opacity: 0.8
                            }
                        },
                        '& .fc-daygrid-day-number': {
                            color: '#003E7E',
                            fontWeight: 500
                        },
                        '& .fc-col-header-cell-cushion': {
                            color: '#003E7E',
                            fontWeight: 600
                        }
                    }}
                >
                    <FullCalendar
                        plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin, rrulePlugin]}
                        initialView={this.props.weekView ? "timeGridWeek" : "dayGridMonth"}
                        initialDate={new Date()}
                        headerToolbar={{
                            left: 'prev,next today',
                            center: 'title',
                            right: 'dayGridMonth,timeGridWeek,timeGridDay'
                        }}
                        events={(info, successCallback, failureCallback) => {
                            const events = this.getEvents();
                            successCallback(events);
                        }}
                        eventClick={this.handleEventClick}
                        height={this.props.calendarHeight || 650}
                        editable={this.state.isAdmin}
                        selectable={this.state.isAdmin}
                        selectMirror={true}
                        dayMaxEvents={true}
                        weekends={true}
                        slotMinTime={this.props.startTime ? `${this.props.startTime}:00:00` : "08:00:00"}
                        slotMaxTime={this.props.endTime ? `${this.props.endTime}:00:00` : "18:00:00"}
                        eventTimeFormat={{
                            hour: '2-digit',
                            minute: '2-digit',
                            meridiem: 'short'
                        }}
                    />
                </Paper>

                {/* Event Details Dialog */}
                <Dialog
                    open={dialogOpen}
                    onClose={this.handleCloseDialog}
                    maxWidth="sm"
                    fullWidth
                    PaperProps={{
                        sx: {
                            borderRadius: 2
                        }
                    }}
                >
                    {selectedEvent && (
                        <>
                            <DialogTitle sx={{
                                backgroundColor: '#003E7E',
                                color: 'white',
                                fontWeight: 600
                            }}>
                                Event Details
                            </DialogTitle>
                            <DialogContent sx={{ mt: 2 }}>
                                {this.state.checkingMembership ? (
                                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4, gap: 2 }}>
                                        <CircularProgress />
                                        <Typography variant="body2" color="text.secondary">
                                            Checking access permissions...
                                        </Typography>
                                    </Box>
                                ) : this.state.isMember ? (
                                    <>
                                        {this.state.editError && (
                                            <Alert severity="error" sx={{ mb: 2 }}>{this.state.editError}</Alert>
                                        )}

                                        {this.state.editing ? (
                                            /* EDIT MODE */
                                            <Box>
                                                {this.state.editFields.isRecurring ? (
                                                    <>
                                                        <TextField
                                                            fullWidth type="datetime-local"
                                                            label="Series start (Eastern time)"
                                                            InputLabelProps={{ shrink: true }}
                                                            value={this.state.editFields.startDateTime}
                                                            onChange={this.handleEditFieldChange('startDateTime')}
                                                            margin="normal" variant="outlined"
                                                        />
                                                        <TextField
                                                            fullWidth label="Duration (HH:MM)"
                                                            value={this.state.editFields.duration}
                                                            onChange={this.handleEditFieldChange('duration')}
                                                            margin="normal" variant="outlined"
                                                            placeholder="01:00"
                                                        />
                                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1, ml: 0.5 }}>
                                                            Repeats {this.recurrenceSummary(selectedEvent.rrule)} (pattern fixed)
                                                        </Typography>
                                                    </>
                                                ) : (
                                                    <>
                                                        <TextField
                                                            fullWidth type="datetime-local"
                                                            label="Start (Eastern time)"
                                                            InputLabelProps={{ shrink: true }}
                                                            value={this.state.editFields.startDate}
                                                            onChange={this.handleEditFieldChange('startDate')}
                                                            margin="normal" variant="outlined"
                                                        />
                                                        <TextField
                                                            fullWidth type="datetime-local"
                                                            label="End (Eastern time)"
                                                            InputLabelProps={{ shrink: true }}
                                                            value={this.state.editFields.endDate}
                                                            onChange={this.handleEditFieldChange('endDate')}
                                                            margin="normal" variant="outlined"
                                                        />
                                                    </>
                                                )}
                                                <TextField
                                                    fullWidth label="Notes" multiline rows={3}
                                                    value={this.state.editFields.notes}
                                                    onChange={this.handleEditFieldChange('notes')}
                                                    margin="normal" variant="outlined"
                                                />
                                                <FormControl fullWidth margin="normal" variant="outlined">
                                                    <InputLabel>Meeting Type</InputLabel>
                                                    <Select
                                                        value={this.state.editFields.meetingType}
                                                        onChange={this.handleEditFieldChange('meetingType')}
                                                        label="Meeting Type"
                                                    >
                                                        <MenuItem value="teams">Teams</MenuItem>
                                                        <MenuItem value="zoom">Zoom</MenuItem>
                                                    </Select>
                                                </FormControl>
                                                {this.state.editFields.meetingType === 'teams' ? (
                                                    <>
                                                        <TextField fullWidth label="Teams Meeting ID" value={this.state.editFields.teamsId} onChange={this.handleEditFieldChange('teamsId')} margin="normal" variant="outlined" />
                                                        <TextField fullWidth label="Teams Passcode" value={this.state.editFields.teamsPasscode} onChange={this.handleEditFieldChange('teamsPasscode')} margin="normal" variant="outlined" />
                                                        <TextField fullWidth label="Teams Link" value={this.state.editFields.teamsLink} onChange={this.handleEditFieldChange('teamsLink')} margin="normal" variant="outlined" />
                                                    </>
                                                ) : (
                                                    <>
                                                        <TextField fullWidth label="Zoom ID" value={this.state.editFields.zoomId} onChange={this.handleEditFieldChange('zoomId')} margin="normal" variant="outlined" />
                                                        <TextField fullWidth label="Zoom Passcode" value={this.state.editFields.zoomPass} onChange={this.handleEditFieldChange('zoomPass')} margin="normal" variant="outlined" />
                                                        <TextField fullWidth label="Zoom Link" value={this.state.editFields.zoomLink} onChange={this.handleEditFieldChange('zoomLink')} margin="normal" variant="outlined" />
                                                        <TextField fullWidth label="Breakout Room" value={this.state.editFields.zoomRoom} onChange={this.handleEditFieldChange('zoomRoom')} margin="normal" variant="outlined" />
                                                    </>
                                                )}
                                            </Box>
                                        ) : (
                                            /* VIEW MODE */
                                            <>
                                        {selectedEvent.notes && (
                                            <>
                                                <Box sx={{ py: 2 }}>
                                                    <Typography variant="body1">
                                                        {selectedEvent.notes}
                                                    </Typography>
                                                </Box>
                                                <Divider sx={{ my: 2 }} />
                                            </>
                                        )}

                                        {selectedEvent.meetingType === 'teams' ? (
                                            <>
                                                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#003E7E' }}>
                                                    Teams Meeting Information
                                                </Typography>

                                                <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                                                    {selectedEvent.teamsId && (
                                                        <li>
                                                            <Typography variant="body2">
                                                                <strong>Meeting ID:</strong> {selectedEvent.teamsId}
                                                            </Typography>
                                                        </li>
                                                    )}
                                                    {selectedEvent.teamsPasscode && (
                                                        <li>
                                                            <Typography variant="body2">
                                                                <strong>Passcode:</strong> {selectedEvent.teamsPasscode}
                                                            </Typography>
                                                        </li>
                                                    )}
                                                    {selectedEvent.teamsLink && (
                                                        <li>
                                                            <Typography variant="body2">
                                                                <MuiLink
                                                                    href={selectedEvent.teamsLink}
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                    sx={{
                                                                        color: '#FF6600',
                                                                        fontWeight: 600,
                                                                        textDecoration: 'none',
                                                                        '&:hover': {
                                                                            textDecoration: 'underline'
                                                                        }
                                                                    }}
                                                                >
                                                                    Join Teams Meeting →
                                                                </MuiLink>
                                                            </Typography>
                                                        </li>
                                                    )}
                                                </Box>
                                            </>
                                        ) : (
                                            <>
                                                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#003E7E' }}>
                                                    Zoom Meeting Information
                                                </Typography>

                                                <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                                                    {selectedEvent.zoomId && (
                                                        <li>
                                                            <Typography variant="body2">
                                                                <strong>Zoom ID:</strong> {selectedEvent.zoomId}
                                                            </Typography>
                                                        </li>
                                                    )}
                                                    {selectedEvent.zoompass && (
                                                        <li>
                                                            <Typography variant="body2">
                                                                <strong>Zoom Passcode:</strong> {selectedEvent.zoompass}
                                                            </Typography>
                                                        </li>
                                                    )}
                                                    {selectedEvent.zoomRoom && (
                                                        <li>
                                                            <Typography variant="body2">
                                                                <strong>Breakout Room:</strong> {selectedEvent.zoomRoom}
                                                            </Typography>
                                                        </li>
                                                    )}
                                                    {selectedEvent.zoomLink && (
                                                        <li>
                                                            <Typography variant="body2">
                                                                <MuiLink
                                                                    href={selectedEvent.zoomLink}
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                    sx={{
                                                                        color: '#FF6600',
                                                                        fontWeight: 600,
                                                                        textDecoration: 'none',
                                                                        '&:hover': {
                                                                            textDecoration: 'underline'
                                                                        }
                                                                    }}
                                                                >
                                                                    Join Zoom Meeting →
                                                                </MuiLink>
                                                            </Typography>
                                                        </li>
                                                    )}
                                                </Box>
                                            </>
                                        )}

                                        {selectedEvent.icsDownloadUrl && (
                                            <>
                                                <Divider sx={{ my: 3 }} />
                                                <Box sx={{ textAlign: 'center' }}>
                                                    <Button
                                                        component={MuiLink}
                                                        href={selectedEvent.icsDownloadUrl}
                                                        download
                                                        variant="outlined"
                                                        sx={{
                                                            borderColor: '#003E7E',
                                                            color: '#003E7E',
                                                            fontWeight: 600,
                                                            '&:hover': {
                                                                borderColor: '#FF6600',
                                                                backgroundColor: 'rgba(255, 102, 0, 0.08)',
                                                                color: '#FF6600'
                                                            }
                                                        }}
                                                    >
                                                        Download Calendar Event (.ics)
                                                    </Button>
                                                </Box>
                                            </>
                                        )}
                                            </>
                                        )}
                                    </>
                                ) : (
                                    <Alert
                                        severity="warning"
                                        icon={<LockIcon />}
                                        sx={{
                                            borderRadius: 2,
                                            '& .MuiAlert-icon': {
                                                fontSize: 32
                                            }
                                        }}
                                    >
                                        <AlertTitle sx={{ fontWeight: 600, fontSize: '1.1rem' }}>
                                            Committee Member Access Required
                                        </AlertTitle>
                                        <Typography variant="body2" sx={{ mb: 2 }}>
                                            This event's full details (including meeting information) are only available to committee members.
                                        </Typography>
                                        <Button
                                            component={MuiLink}
                                            href={`/group/requestaccess/${this.state.currentGroupId}`}
                                            variant="contained"
                                            sx={{
                                                backgroundColor: '#FF6600',
                                                color: 'white',
                                                fontWeight: 600,
                                                mt: 1,
                                                textDecoration: 'none',
                                                '&:hover': {
                                                    backgroundColor: '#e55a00',
                                                    textDecoration: 'none'
                                                }
                                            }}
                                        >
                                            Request Committee Access
                                        </Button>
                                    </Alert>
                                )}
                            </DialogContent>
                            <DialogActions sx={{ p: 2, justifyContent: 'space-between' }}>
                                <Box>
                                    {this.state.isChairOrCoChair && !this.state.checkingMembership && !this.state.editing && (
                                        <Button
                                            onClick={this.handleEditToggle}
                                            startIcon={<EditIcon />}
                                            sx={{ color: '#003E7E' }}
                                        >
                                            Edit
                                        </Button>
                                    )}
                                </Box>
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                    {this.state.editing ? (
                                        <>
                                            <Button onClick={() => this.setState({ editing: false, editError: '' })}>
                                                Cancel
                                            </Button>
                                            <Button
                                                onClick={this.handleEditSave}
                                                variant="contained"
                                                disabled={this.state.editSaving}
                                                sx={{ backgroundColor: '#003E7E', '&:hover': { backgroundColor: '#FF6600' } }}
                                            >
                                                {this.state.editSaving ? 'Saving...' : 'Save'}
                                            </Button>
                                        </>
                                    ) : (
                                        <Button
                                            onClick={this.handleCloseDialog}
                                            variant="contained"
                                            sx={{ backgroundColor: '#003E7E', '&:hover': { backgroundColor: '#FF6600' } }}
                                        >
                                            Close
                                        </Button>
                                    )}
                                </Box>
                            </DialogActions>
                        </>
                    )}
                </Dialog>
            </Box>
        )
    }
};

export default EventCalendar;
